import { DatabaseOperationsService } from './api/services/DatabaseOperationsService'
import { OpenAPI } from './api/core/OpenAPI'
import { encryptData, decryptData } from '@/utils/encryption'
import { getAgentEncryptionSettings } from '@/utils/agentEncryption'
import { v7 as uuidv7 } from 'uuid'
import { fetchDataSourceDirect } from '@/stores/dataSourceStore'
import { stripComments } from '@/utils/sqlParser'

// Type definitions for command data structures
export interface BaseCommandData {
  nonce?: string
  timestamp?: number
  [key: string]: unknown
}

interface EncryptedPayloadBody {
  encrypted_payload: string
}

interface AgentCommand {
  type: string
  timeout: number
  data: BaseCommandData
}

interface AgentErrorResponse {
  status: 'error'
  message?: string
  error_code?: string
}

interface TableDataResponse {
  type: string
  rows?: Record<string, unknown>[]
  columns?: {
    name: string
    type: string
    [key: string]: unknown
  }[]
  [key: string]: unknown
}

interface ListResponse<T> {
  data?: T[]
  databases?: T[]
  tables?: T[]
  schemas?: T[]
  functions?: T[]
  procedures?: T[]
  triggers?: T[]
  views?: T[]
  indexes?: T[]
  sequences?: T[]
  events?: T[]
  constraints?: T[]
  columns?: T[]
  users?: T[]
  sessions?: T[]
  [key: string]: T[] | undefined
}

interface EventDetailsResponse {
  type: 'get_event_details'
  event_details: unknown
}

interface ActiveSessionsResponse {
  type: 'get_active_sessions'
  data?: {
    sessions: {
      id: string | number
      user?: string
      database?: string
      [key: string]: unknown
    }[]
  }
}

// Type definition for encryption settings
interface EncryptionSettings {
  encryptionEnabled: boolean
  encryptionKey: string
}

// Type guard to check if environment variable is a valid string
function isValidString(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0
}

// Type guard to check if response has error status
function isErrorResponse(response: unknown): response is AgentErrorResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'status' in response &&
    (response as { status: unknown }).status === 'error'
  )
}

// Type guard to check if response is encrypted
function isEncryptedResponse(response: unknown): response is { encrypted_payload: string } {
  return (
    response !== null &&
    typeof response === 'object' &&
    'encrypted_payload' in response &&
    typeof (response as { encrypted_payload: unknown }).encrypted_payload === 'string'
  )
}

/**
 * Centralized service for agent proxy operations
 * This replaces the individual service classes with a unified agent-based approach
 */
export class AgentProxyService {
  /**
   * Execute a command through the agent proxy with proper encryption handling
   */
  private static async executeCommand(
    datasourceId: string,
    type: string,
    commandData: BaseCommandData,
    timeout = 30,
    suppressToast = false
  ): Promise<unknown> {
    // Get encryption settings for the datasource
    const { encryptionEnabled, encryptionKey } = await this.getEncryptionSettings(datasourceId)
    
    const command: AgentCommand = {
      type,
      timeout,
      data: {
        ...commandData,
        nonce: uuidv7(),
        timestamp: Math.floor(Date.now() / 1000)
      }
    }

    if (type === 'update_rows') {
      console.log('🔍 Final command being sent to server:', JSON.stringify(command, null, 2))
    }

    let requestBody: EncryptedPayloadBody

    if (encryptionEnabled && encryptionKey) {
      // Encrypt the command - convert to plain object for encryption
      const commandObj: Record<string, unknown> = {
        type: command.type,
        timeout: command.timeout,
        data: command.data
      }
      const encryptedPayload = await encryptData(commandObj, encryptionKey)
      requestBody = {
        encrypted_payload: encryptedPayload
      }
    } else {
      // For unencrypted requests (fallback)
      requestBody = {
        encrypted_payload: JSON.stringify(command)
      }
    }

    // Store original headers configuration
    const originalHeaders = OpenAPI.HEADERS
    
    // Create enhanced headers function that includes debugging header
    OpenAPI.HEADERS = async (options) => {
      const headers: Record<string, string> = {}
      
      // Apply original headers if they exist
      if (originalHeaders) {
        const originalResult = typeof originalHeaders === 'function' 
          ? await originalHeaders(options)
          : originalHeaders
        Object.assign(headers, originalResult)
      }
      
      // Add debugging header for agent proxy requests
      if (options.url.includes('/api/agent-proxy/')) {
        headers['agent-proxy-command'] = type
      }
      
      return headers
    }

    try {
      // Log the agent proxy command type for debugging purposes
      console.log(`🔍 [AgentProxyService] Executing command: ${type} for datasource: ${datasourceId}`)

      // The API expects a command object with encrypted_payload, which matches our EncryptedPayloadBody type
      // We cast to unknown first to satisfy TypeScript's type checking for the union type
      const response = await DatabaseOperationsService.agentProxyCommand({
        datasourceId,
        requestBody: requestBody as unknown as Parameters<typeof DatabaseOperationsService.agentProxyCommand>[0]['requestBody']
      })

      // Handle encrypted response if needed
      let finalResponse: unknown
      
      // Always try to decrypt if we get an encrypted response, regardless of encryption settings
      if (isEncryptedResponse(response) && encryptionKey) {
        finalResponse = await this.decryptResponse(response, encryptionKey);
      } else if (encryptionEnabled && encryptionKey) {
        finalResponse = await this.decryptResponse(response, encryptionKey);
      } else {
        finalResponse = response;
      }

      // Check for agent proxy errors and show user-friendly messages
      if (isErrorResponse(finalResponse)) {
        const errorMessage = finalResponse.message || 'An unexpected error occurred'
        const errorCode = finalResponse.error_code || 'UNKNOWN_ERROR'

        // Only show toast if not suppressed
        if (!suppressToast) {
          // Import toast dynamically to avoid circular dependencies
          const toast = (await import('react-hot-toast')).default
          toast.error(`Error: ${errorMessage}`)
        }

        // Throw error to maintain existing error handling in components
        // Attach additional diagnostic fields (e.g., dependencies/remediation) for richer UI handling
        const error = new Error(errorMessage) as Error & Record<string, unknown>
        error.name = errorCode
        if (typeof finalResponse === 'object' && finalResponse !== null) {
          // Access properties safely without type assertion
          if ('dependencies' in finalResponse && finalResponse.dependencies !== undefined) {
            error.dependencies = finalResponse.dependencies
          }
          if ('remediation' in finalResponse && finalResponse.remediation !== undefined) {
            error.remediation = finalResponse.remediation
          }
          if ('type' in finalResponse && finalResponse.type !== undefined) {
            error.type = finalResponse.type
          }
        }
        throw error
      }

      return finalResponse
    } finally {
      // Always restore original headers configuration
      OpenAPI.HEADERS = originalHeaders
    }
  }

  /**
   * Get encryption settings for a datasource
   */
  private static async getEncryptionSettings(datasourceId: string): Promise<EncryptionSettings> {    
    // Default to disabled encryption
    let encryptionEnabled = false
    let encryptionKey = ''

    try {
      const datasource = await fetchDataSourceDirect(datasourceId)
      
      if (datasource && datasource.agentId) {
        const agentEncryption = getAgentEncryptionSettings(datasource.agentId)
        // Use agent-specific encryption settings
        encryptionEnabled = agentEncryption.enabled
        if (isValidString(agentEncryption.key)) {
          encryptionKey = agentEncryption.key
        }
      } else {
        console.log(`🔍 [AgentProxyService] No agentId found for datasource ${datasourceId}`)
      }
    } catch (error) {
      console.warn('Failed to fetch datasource for encryption settings, using defaults:', error)
    }

    return { encryptionEnabled, encryptionKey }
  }

  /**
   * Decrypt response if needed and normalize format
   */
  private static async decryptResponse(response: unknown, encryptionKey: string): Promise<unknown> {
    let decryptedData: unknown = response
    

    try {
      // Decrypt if needed with proper type checking
      if (isEncryptedResponse(response)) {
        const decrypted = await decryptData(response.encrypted_payload, encryptionKey)
        decryptedData = typeof decrypted === 'string' ? JSON.parse(decrypted) as unknown : decrypted
      } else if (typeof response === 'string') {
        const decrypted = await decryptData(response, encryptionKey)
        decryptedData = typeof decrypted === 'string' ? JSON.parse(decrypted) as unknown : decrypted
      }
      
    } catch (error) {
      console.error(`🔍 [AgentProxyService] Decryption failed:`, error)
      // Return the original response if decryption fails
      decryptedData = response
    }

    // Normalize response format for consistency
    const normalized = this.normalizeResponse(decryptedData)
    return normalized
  }

  /**
   * Normalize response format to ensure consistency
   */
  private static normalizeResponse(response: unknown): unknown {
    if (!response) return response
    
    // If response is already an array, return as-is
    if (Array.isArray(response)) {
      return response
    }
    
    // Handle get_column_info response format specifically
    if (typeof response === 'object' && 'type' in response && (response as { type: string }).type === 'get_column_info') {
      const columnResponse = response as { columns?: unknown[] }
      if (columnResponse.columns && Array.isArray(columnResponse.columns)) {
        return columnResponse.columns
      }
    }
    
    // For complex responses that need to preserve structure (table data, etc.)
    // Check if this is a structured response that should be kept intact
    const typedResponse = response as { type?: string }
    if (typedResponse.type && (
      typedResponse.type === 'get_table_data' ||
      typedResponse.type === 'get_table_schema' ||
      typedResponse.type === 'execute_query' ||
      typedResponse.type === 'explain_query_plan' ||
      typedResponse.type === 'get_sequence_definition' ||
      typedResponse.type === 'drop_sequence'
    )) {
      // Return structured responses as-is to preserve rows, columns, pagination, etc.
      return response
    }
    
    // Handle get_event_details response format specifically
    if (typeof response === 'object' && 'type' in response && (response as { type: string }).type === 'get_event_details') {
      const eventResponse = response as EventDetailsResponse
      if (eventResponse.event_details) {
        return eventResponse.event_details
      }
    }
    
    // Handle get_active_sessions response format specifically
    if (typeof response === 'object' && 'type' in response && (response as { type: string }).type === 'get_active_sessions') {
      const sessionsResponse = response as ActiveSessionsResponse
      if (sessionsResponse.data?.sessions && Array.isArray(sessionsResponse.data.sessions)) {
        return sessionsResponse.data.sessions
      }
    }
    
    // If response has data property with array, extract it for simple list responses
    if (typeof response === 'object' && 'data' in response) {
      const listResponse = response as ListResponse<unknown>
      if (listResponse.data && Array.isArray(listResponse.data)) {
        return listResponse.data
      }
    }
    
    // Handle specific agent response formats for simple list operations
    const listResponse = response as ListResponse<unknown>
    if (listResponse.databases && Array.isArray(listResponse.databases)) {
      return listResponse.databases
    }
    if (listResponse.tables && Array.isArray(listResponse.tables)) {
      return listResponse.tables
    }
    if (listResponse.schemas && Array.isArray(listResponse.schemas)) {
      return listResponse.schemas
    }
    if (listResponse.functions && Array.isArray(listResponse.functions)) {
      return listResponse.functions
    }
    if (listResponse.procedures && Array.isArray(listResponse.procedures)) {
      return listResponse.procedures
    }
    if (listResponse.triggers && Array.isArray(listResponse.triggers)) {
      return listResponse.triggers
    }
    if (listResponse.views && Array.isArray(listResponse.views)) {
      return listResponse.views
    }
    if (listResponse.indexes && Array.isArray(listResponse.indexes)) {
      return listResponse.indexes
    }
    if (listResponse.sequences && Array.isArray(listResponse.sequences)) {
      return listResponse.sequences
    }
    if (listResponse.events && Array.isArray(listResponse.events)) {
      return listResponse.events
    }
    if (listResponse.constraints && Array.isArray(listResponse.constraints)) {
      return listResponse.constraints
    }
    // Only extract columns array if this is not a structured response
    const tableResponse = response as TableDataResponse
    if (listResponse.columns && Array.isArray(listResponse.columns) && !tableResponse.rows) {
      return listResponse.columns
    }
    if (listResponse.users && Array.isArray(listResponse.users)) {
      return listResponse.users
    }
    if (listResponse.sessions && Array.isArray(listResponse.sessions)) {
      return listResponse.sessions
    }
    
    // Return as-is if no normalization needed
    return response
  }

  // Database operations
  static async getDatabases(datasourceId: string, limit?: number, page?: number) {
    return this.executeCommand(datasourceId, 'get_databases', {
      limit: limit || 100,
      page: page || 1
    })
  }

  static async getSchemas(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'get_schemas', {
      database_name: databaseName,
      limit: 100
    })
  }

  static async getTables(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_tables', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getTableSchema(datasourceId: string, databaseName: string, tableName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_table_schema', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName
    })
  }

  static async createDatabase(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'create_database', {
      database_name: databaseName
    })
  }

  static async dropDatabase(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'drop_database', {
      database_name: databaseName
    })
  }

  // Schema operations
  static async createSchema(datasourceId: string, databaseName: string, schemaData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_schema', {
      database_name: databaseName,
      ...schemaData
    })
  }

  static async dropSchema(datasourceId: string, databaseName: string, schemaName: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_schema', {
      database_name: databaseName,
      schema_name: schemaName,
      cascade: cascade
    })
  }

  // Table operations
  static async createTable(datasourceId: string, databaseName: string, tableData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_table', {
      database_name: databaseName,
      ...tableData
    })
  }

  static async dropTable(datasourceId: string, databaseName: string, tableName: string, options?: {
    schemaName?: string
    cascade?: boolean
  }) {
    return this.executeCommand(datasourceId, 'drop_table', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      cascade: options?.cascade
    })
  }

  static async getTableData(datasourceId: string, databaseName: string, tableName: string, options?: {
    schemaName?: string
    limit?: number
    page?: number
    where_clause?: string
    order_by?: string
    suppressToast?: boolean
  }) {
    return this.executeCommand(datasourceId, 'get_table_data', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      limit: options?.limit || 100,
      page: options?.page || 1,
      where_clause: options?.where_clause,
      order_by: options?.order_by
    }, 30, options?.suppressToast || false)
  }

  // Data operations
  static async insertRows(datasourceId: string, databaseName: string, tableName: string, data: unknown[], options?: {
    schemaName?: string
  }) {
    return this.executeCommand(datasourceId, 'insert_rows', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      rows: data
    })
  }

  static async updateRows(datasourceId: string, databaseName: string, tableName: string, data: unknown[] | Record<string, unknown>, whereConditions: Record<string, unknown>, options?: {
    schemaName?: string
    rowId?: string
  }) {
    const commandData: BaseCommandData = {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      row_data: Array.isArray(data) ? data[0] : data,  // API spec expects single object, not array
      where_conditions: whereConditions
    }

    // If rowId is provided, use it in where_conditions
    if (options?.rowId) {
      // Find primary key name from row_data and use it for where_conditions
      const rowData = commandData.row_data as Record<string, unknown>
      const primaryKeyName = Object.keys(rowData).find(key => 
        key.toLowerCase().includes('id') || key.toLowerCase().includes('_id')
      ) || 'id'
      commandData.where_conditions = { [primaryKeyName]: options.rowId }
    }

    console.log('🔍 updateRows sending to server:', {
      datasourceId,
      commandType: 'update_rows',
      commandData,
      originalData: data,
      'API format': 'row_data + where_conditions'
    })

    return this.executeCommand(datasourceId, 'update_rows', commandData)
  }

  static async deleteRows(datasourceId: string, databaseName: string, tableName: string, whereConditions: Record<string, unknown>, options?: {
    schemaName?: string
  }) {
    return this.executeCommand(datasourceId, 'delete_rows', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: options?.schemaName,
      where_conditions: whereConditions
    })
  }

  // Query operations
  static async executeQuery(datasourceId: string, query: string, databaseName?: string, suppressToast = false) {
    return this.executeCommand(datasourceId, 'execute_query', {
      query,
      database_name: databaseName
    }, 30, suppressToast)
  }

  static async explainQueryPlan(datasourceId: string, query: string, databaseName?: string, suppressToast = false) {
    const cleanQuery = stripComments(query)
    return this.executeCommand(datasourceId, 'explain_query_plan', {
      query: cleanQuery,
      database_name: databaseName
    }, 30, suppressToast)
  }

  // Session operations
  static async getActiveSessions(datasourceId: string, databaseFilter?: string) {
    return this.executeCommand(datasourceId, 'get_active_sessions', {
      database_filter: databaseFilter
    })
  }

  static async killSession(datasourceId: string, sessionId: string) {
    return this.executeCommand(datasourceId, 'kill_session', {
      session_id: sessionId
    })
  }

  // Event operations
  static async getEvents(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'get_events', {
      database_name: databaseName,
      limit: 500
    })
  }

  static async createEvent(datasourceId: string, databaseName: string, eventData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_event', {
      database_name: databaseName,
      ...eventData
    })
  }

  static async getEventDetails(datasourceId: string, databaseName: string, eventName: string) {
    return this.executeCommand(datasourceId, 'get_event_details', {
      database_name: databaseName,
      event_name: eventName
    })
  }

  static async alterEvent(datasourceId: string, databaseName: string, eventName: string, eventData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_event', {
      database_name: databaseName,
      event_name: eventName,
      ...eventData
    })
  }

  static async dropEvent(datasourceId: string, databaseName: string, eventName: string) {
    return this.executeCommand(datasourceId, 'drop_event', {
      database_name: databaseName,
      event_name: eventName
    })
  }

  // Column operations
  static async getColumnInfo(datasourceId: string, databaseName: string, tableName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_column_info', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName
    })
  }

  static async addColumn(datasourceId: string, databaseName: string, tableName: string, columnData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'add_column', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      column: columnData
    })
  }

  static async modifyColumn(datasourceId: string, databaseName: string, tableName: string, columnName: string, newColumnData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'modify_column', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      column_name: columnName,
      new_column: newColumnData
    })
  }

  static async dropColumn(datasourceId: string, databaseName: string, tableName: string, columnName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'drop_column', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      column_name: columnName
    })
  }

  // Index operations
  static async createIndex(datasourceId: string, databaseName: string, indexData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_index', {
      database_name: databaseName,
      ...indexData
    })
  }

  static async dropIndex(datasourceId: string, databaseName: string, indexName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'drop_index', {
      database_name: databaseName,
      index_name: indexName,
      schema_name: schemaName
    })
  }

  static async getIndexes(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_indexes', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getTableIndexes(datasourceId: string, databaseName: string, tableName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_table_indexes', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName
    })
  }

  static async getIndexDefinition(datasourceId: string, databaseName: string, indexName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_index_definition', {
      database_name: databaseName,
      index_name: indexName,
      schema_name: schemaName
    })
  }

  // User operations
  static async getDatabaseUsers(datasourceId: string, databaseName: string) {
    return this.executeCommand(datasourceId, 'get_database_users', {
      database_name: databaseName,
      limit: 500
    })
  }

  static async createUser(datasourceId: string, databaseName: string, userData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_user', {
      database_name: databaseName,
      ...userData
    })
  }

  static async alterUser(datasourceId: string, databaseName: string, username: string, userData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_user', {
      database_name: databaseName,
      username,
      ...userData
    })
  }

  static async dropUser(datasourceId: string, databaseName: string, username: string) {
    return this.executeCommand(datasourceId, 'drop_user', {
      database_name: databaseName,
      username
    })
  }

  static async getUserDetails(datasourceId: string, databaseName: string, username: string, includeDependencies?: boolean) {
    return this.executeCommand(datasourceId, 'get_user_details', {
      database_name: databaseName,
      username,
      include_dependencies: includeDependencies
    })
  }

  // Remove user membership from role (PostgreSQL specific)
  static async removeUserMembership(datasourceId: string, databaseName: string, username: string, roleName: string) {
    return this.executeCommand(datasourceId, 'remove_user_membership', {
      database_name: databaseName,
      username,
      role_name: roleName
    })
  }

  // Reassign owned objects from one user to another (PostgreSQL specific)
  static async reassignOwnedBy(datasourceId: string, databaseName: string, fromUsername: string, toUsername: string) {
    // Send both username- and role-style fields for maximum backend compatibility
    return this.executeCommand(datasourceId, 'reassign_owned_by', {
      database_name: databaseName,
      from_username: fromUsername,
      to_username: toUsername,
      // Some agent versions expect role field names
      from_role: fromUsername,
      to_role: toUsername
    })
  }

  // Functions
  static async getFunctions(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_functions', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getFunctionDefinition(datasourceId: string, databaseName: string, functionName: string, schemaName?: string, functionArguments?: string) {
    return this.executeCommand(datasourceId, 'get_function_definition', {
      database_name: databaseName,
      function_name: functionName,
      schema_name: schemaName,
      ...(functionArguments ? { arguments: functionArguments } : {})
    })
  }

  // Procedures
  static async getProcedures(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_procedures', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getProcedureDefinition(datasourceId: string, databaseName: string, procedureName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_procedure_definition', {
      database_name: databaseName,
      procedure_name: procedureName,
      schema_name: schemaName
    })
  }

  // Triggers
  static async getTriggers(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_triggers', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getTriggerDefinition(datasourceId: string, databaseName: string, triggerName: string) {
    return this.executeCommand(datasourceId, 'get_trigger_definition', {
      database_name: databaseName,
      trigger_name: triggerName
    })
  }

  // Views
  static async getViews(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_views', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  static async getViewDefinition(datasourceId: string, databaseName: string, viewName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_view_definition', {
      database_name: databaseName,
      view_name: viewName,
      schema_name: schemaName
    })
  }

  // Sequences
  static async getSequences(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_sequences', {
      database_name: databaseName,
      schema_name: schemaName,
      limit: 500
    })
  }

  // Constraints
  static async getTableConstraints(datasourceId: string, databaseName: string, tableName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_table_constraints', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName
    })
  }

  static async getForeignKeys(datasourceId: string, databaseName: string, tableName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_foreign_keys', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName
    })
  }

  // Database details
  static async getDatabaseDetails(datasourceId: string, databaseName: string) {
    // Suppress popup toast for database details errors; let UI render inline errors
    return this.executeCommand(
      datasourceId,
      'get_database_details',
      {
        database_name: databaseName
      },
      30,
      true
    )
  }

  // Test connection
  static async testConnection(datasourceId: string) {
    return this.executeCommand(datasourceId, 'test_connection', {
      datasource_id: datasourceId
    })
  }

  // Additional operations for complete coverage
  
  // Create operations
  static async createFunction(datasourceId: string, databaseName: string, functionData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_function', {
      database_name: databaseName,
      schema_name: schemaName,
      ...functionData
    })
  }

  static async createProcedure(datasourceId: string, databaseName: string, procedureData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_procedure', {
      database_name: databaseName,
      schema_name: schemaName,
      ...procedureData
    })
  }

  static async createTrigger(datasourceId: string, databaseName: string, triggerData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_trigger', {
      database_name: databaseName,
      schema_name: schemaName,
      ...triggerData
    })
  }

  static async createView(datasourceId: string, databaseName: string, viewData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'create_view', {
      database_name: databaseName,
      schema_name: schemaName,
      ...viewData
    })
  }

  static async createSequence(datasourceId: string, databaseName: string, sequenceData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_sequence', {
      database_name: databaseName,
      ...sequenceData
    })
  }

  // Drop operations
  static async dropFunction(datasourceId: string, databaseName: string, functionName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'drop_function', {
      database_name: databaseName,
      function_name: functionName,
      schema_name: schemaName
    })
  }

  static async dropProcedure(datasourceId: string, databaseName: string, procedureName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'drop_procedure', {
      database_name: databaseName,
      procedure_name: procedureName,
      schema_name: schemaName
    })
  }

  static async dropTrigger(datasourceId: string, databaseName: string, triggerName: string) {
    return this.executeCommand(datasourceId, 'drop_trigger', {
      database_name: databaseName,
      trigger_name: triggerName
    })
  }

  static async dropView(datasourceId: string, databaseName: string, viewName: string, schemaName?: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_view', {
      database_name: databaseName,
      view_name: viewName,
      schema_name: schemaName,
      cascade: cascade
    })
  }

  static async dropSequence(datasourceId: string, databaseName: string, sequenceName: string, schemaName?: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_sequence', {
      database_name: databaseName,
      sequence_name: sequenceName,
      schema_name: schemaName || 'public',
      cascade: cascade
    })
  }

  // Alter operations
  static async alterTable(datasourceId: string, databaseName: string, tableName: string, alterData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'alter_table', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      ...alterData
    })
  }

  static async alterFunction(datasourceId: string, databaseName: string, functionName: string, functionData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'alter_function', {
      database_name: databaseName,
      function_name: functionName,
      schema_name: schemaName,
      ...functionData
    })
  }

  static async alterProcedure(datasourceId: string, databaseName: string, procedureName: string, procedureData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'alter_procedure', {
      database_name: databaseName,
      procedure_name: procedureName,
      schema_name: schemaName,
      ...procedureData
    })
  }

  static async alterTrigger(datasourceId: string, databaseName: string, triggerName: string, triggerData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_trigger', {
      database_name: databaseName,
      trigger_name: triggerName,
      ...triggerData
    })
  }

  static async alterView(datasourceId: string, databaseName: string, viewName: string, viewData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'alter_view', {
      database_name: databaseName,
      view_name: viewName,
      schema_name: schemaName,
      ...viewData
    })
  }

  // Additional get operations removed duplicate

  static async getSequenceDefinition(datasourceId: string, databaseName: string, sequenceName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_sequence_definition', {
      database_name: databaseName,
      sequence_name: sequenceName,
      schema_name: schemaName || 'public'
    })
  }

  // Import/Export operations
  static async importData(datasourceId: string, databaseName: string, importData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'import_data', {
      database_name: databaseName,
      ...importData
    })
  }

  static async exportData(datasourceId: string, databaseName: string, exportData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'export_data', {
      database_name: databaseName,
      ...exportData
    })
  }

  // Grant/Revoke privileges
  static async grantPrivileges(datasourceId: string, databaseName: string, username: string, privileges: BaseCommandData) {
    return this.executeCommand(datasourceId, 'grant_privileges', {
      database_name: databaseName,
      username,
      ...privileges
    })
  }

  static async revokePrivileges(datasourceId: string, databaseName: string, username: string, privileges: BaseCommandData) {
    return this.executeCommand(datasourceId, 'revoke_privileges', {
      database_name: databaseName,
      username,
      ...privileges
    })
  }

  // Role management
  static async getRoles(datasourceId: string, databaseName: string, limit?: number, offset?: number) {
    return this.executeCommand(datasourceId, 'get_roles', {
      database_name: databaseName,
      limit,
      offset
    })
  }

  static async createRole(datasourceId: string, databaseName: string, roleData: BaseCommandData) {
    console.log('🎭 Creating role with data:', {
      datasourceId,
      databaseName,
      roleData
    })
    
    const result = await this.executeCommand(datasourceId, 'create_role', {
      database_name: databaseName,
      ...roleData
    })
    
    console.log('🎭 Create role response:', result)
    return result
  }

  static async dropRole(datasourceId: string, databaseName: string, roleName: string) {
    return this.executeCommand(datasourceId, 'drop_role', {
      database_name: databaseName,
      role_name: roleName
    })
  }

  static async alterRole(datasourceId: string, databaseName: string, roleName: string, roleData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'alter_role', {
      database_name: databaseName,
      role_name: roleName,
      ...roleData
    })
  }

  static async grantRole(datasourceId: string, databaseName: string, roleName: string, username: string) {
    return this.executeCommand(datasourceId, 'grant_role', {
      database_name: databaseName,
      role_name: roleName,
      username
    })
  }

  static async revokeRole(datasourceId: string, databaseName: string, roleName: string, username: string) {
    return this.executeCommand(datasourceId, 'revoke_role', {
      database_name: databaseName,
      role_name: roleName,
      username
    })
  }

  // Get grants for a user or role
  static async getGrants(
    datasourceId: string,
    databaseName: string,
    username: string,
    granteeType?: 'user' | 'role',
    host?: string
  ) {
    return this.executeCommand(datasourceId, 'get_grants', {
      database_name: databaseName,
      username,
      grantee_type: granteeType,
      host
    })
  }

  // Default privileges management
  static async setDefaultPrivileges(datasourceId: string, databaseName: string, defaultPrivilegesData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'set_default_privileges', {
      database_name: databaseName,
      ...defaultPrivilegesData
    })
  }

  static async getDefaultPrivileges(datasourceId: string, databaseName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_default_privileges', {
      database_name: databaseName,
      schema_name: schemaName
    })
  }

  static async revokeDefaultPrivileges(datasourceId: string, databaseName: string, defaultPrivilegesData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'revoke_default_privileges', {
      database_name: databaseName,
      ...defaultPrivilegesData
    })
  }

  // Constraint operations
  static async addConstraint(datasourceId: string, databaseName: string, tableName: string, constraintData: BaseCommandData, schemaName?: string) {
    return this.executeCommand(datasourceId, 'add_constraint', {
      database_name: databaseName,
      table_name: tableName,
      schema_name: schemaName,
      ...constraintData
    })
  }

  static async dropConstraint(datasourceId: string, databaseName: string, tableName: string, constraintName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'drop_constraint', {
      database_name: databaseName,
      table_name: tableName,
      constraint_name: constraintName,
      schema_name: schemaName
    })
  }

  static async getConstraintDetails(datasourceId: string, databaseName: string, tableName: string, constraintName: string, schemaName?: string) {
    return this.executeCommand(datasourceId, 'get_constraint_details', {
      database_name: databaseName,
      table_name: tableName,
      constraint_name: constraintName,
      schema_name: schemaName
    })
  }

  // Additional index operations
  static async getIndexesBySchema(datasourceId: string, databaseName: string, schemaName: string) {
    return this.executeCommand(datasourceId, 'get_indexes_by_schema', {
      database_name: databaseName,
      schema_name: schemaName
    })
  }

  // Connection info
  static async getConnectionInfo(datasourceId: string) {
    return this.executeCommand(datasourceId, 'get_connection_info', {})
  }

  // Backup operations
  static async createBackup(datasourceId: string, databaseName: string, backupData: BaseCommandData) {
    return this.executeCommand(datasourceId, 'create_backup', {
      database_name: databaseName,
      ...backupData
    })
  }

  // PostgreSQL Extension operations
  static async getExtensions(datasourceId: string, databaseName: string, filter?: string, limit?: number, page?: number) {
    return this.executeCommand(datasourceId, 'get_extensions', {
      database_name: databaseName,
      filter,
      limit,
      page
    })
  }

  static async createExtension(datasourceId: string, databaseName: string, extensionName: string, options?: {
    schemaName?: string
    version?: string
  }) {
    return this.executeCommand(datasourceId, 'create_extension', {
      database_name: databaseName,
      extension_name: extensionName,
      schema_name: options?.schemaName,
      version: options?.version
    })
  }

  static async alterExtension(datasourceId: string, databaseName: string, extensionName: string, options?: {
    newVersion?: string
    newSchema?: string
  }) {
    return this.executeCommand(datasourceId, 'alter_extension', {
      database_name: databaseName,
      extension_name: extensionName,
      new_version: options?.newVersion,
      new_schema: options?.newSchema
    })
  }

  static async dropExtension(datasourceId: string, databaseName: string, extensionName: string, cascade?: boolean) {
    return this.executeCommand(datasourceId, 'drop_extension', {
      database_name: databaseName,
      extension_name: extensionName,
      cascade: cascade ?? false
    })
  }
}